import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { FaArrowLeft, FaPause, FaPlay } from "react-icons/fa";
import Handler from "../../Helpers/Handler";
import useGoogleMaps from "../../Helpers/useGoogleMaps";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton } from "../../Components/Needed";
import { MAP_OPTIONS, MAP_STYLE, SHIFT_COLOR, TASK_COLOR, durationText, fitTo, toLatLng, toLocalInput } from "./trackingShared";

const SPEEDS = [1, 4, 16];

// Consecutive points on a task vs. off a task become separate lines, so the route shows
// where the delivery/pick-up legs were. The first point of a new segment repeats the last
// of the previous one so the lines join up.
const segmentsOf = (points) => {
    const out = [];
    let current = null;
    points.forEach((p, i) => {
        const onTask = !!p.taskId;
        const ll = toLatLng(p);
        if (!current || current.onTask !== onTask) {
            const prev = i > 0 ? toLatLng(points[i - 1]) : null;
            current = { onTask, path: prev ? [prev] : [] };
            out.push(current);
        }
        if (ll) current.path.push(ll);
    });
    return out.filter((s) => s.path.length > 1);
};

const StaffRoute = () => {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const taskId = searchParams.get("taskId") || "";
    const { isLoaded, loadError } = useGoogleMaps();
    const mapRef = useRef(null);

    const [name, setName] = useState("");
    const [from, setFrom] = useState(() => searchParams.get("from") || toLocalInput(new Date(new Date().setHours(0, 0, 0, 0))));
    const [to, setTo] = useState(() => searchParams.get("to") || toLocalInput(new Date()));
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [cursor, setCursor] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(4);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "GET", url: `/admin/staff/${staffId}` });
            setName(response?.success ? response?.data?.data?.name || "" : "");
        })();
    }, [staffId]);

    const load = useCallback(async (range = { from, to }) => {
        setLoading(true);
        setError("");
        setPlaying(false);
        const start = new Date(range.from);
        const end = new Date(range.to);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
            setError("Choose a start time before the end time");
            setLoading(false);
            return;
        }
        const response = await Handler({
            method: "GET",
            url: `/admin/location/trail/${staffId}`,
            params: { from: start.toISOString(), to: end.toISOString(), ...(taskId ? { taskId } : {}) },
        });
        if (response?.success) {
            setResult(response?.data?.data || null);
            setCursor(0);
        } else {
            setResult(null);
            setError(textCapitalize(response?.data?.message || "Could not load the route"));
        }
        setLoading(false);
    }, [staffId, taskId, from, to]);

    // Load when the person or task changes; changing the dates only loads on "Show route".
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staffId, taskId]);

    const points = useMemo(() => (result?.points || []).filter((p) => toLatLng(p)), [result]);
    const segments = useMemo(() => segmentsOf(points), [points]);
    const here = points[Math.min(cursor, Math.max(points.length - 1, 0))] || null;

    useEffect(() => {
        if (!isLoaded || !points.length) return;
        fitTo(mapRef.current, window.google, points.map(toLatLng));
    }, [isLoaded, points]);

    // Playback: step one fix every 120 ms / speed.
    useEffect(() => {
        if (!playing || !points.length) return undefined;
        const timer = setInterval(() => {
            setCursor((c) => {
                if (c >= points.length - 1) { setPlaying(false); return c; }
                return c + 1;
            });
        }, Math.max(15, 480 / speed));
        return () => clearInterval(timer);
    }, [playing, speed, points.length]);

    const preset = (kind) => {
        const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
        let range;
        if (kind === "today") range = { from: toLocalInput(startOfToday), to: toLocalInput(new Date()) };
        else if (kind === "yesterday") range = { from: toLocalInput(new Date(startOfToday.getTime() - 86400000)), to: toLocalInput(new Date(startOfToday.getTime() - 60000)) };
        else range = { from: toLocalInput(new Date(Date.now() - 86400000)), to: toLocalInput(new Date()) };
        setFrom(range.from);
        setTo(range.to);
        load(range);
    };

    return (
        <>
            <button type="button" className="btn btn-link text-decoration-none mb-2 d-inline-flex align-items-center gap-2" onClick={() => navigate(-1)}>
                <FaArrowLeft /> Back
            </button>
            <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                <div className="me-auto">
                    <h4 className="fw-bold mb-0">Route history</h4>
                    <small className="text-muted">
                        <Link to={`/staff/${staffId}`}>{name || staffId}</Link>
                        {taskId ? <> · task <Link to={`/tasks/${taskId}`}>{taskId}</Link> only</> : null}
                    </small>
                </div>
                <div>
                    <label className="form-label small mb-1" htmlFor="st-from">From</label>
                    <input id="st-from" type="datetime-local" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
                </div>
                <div>
                    <label className="form-label small mb-1" htmlFor="st-to">To</label>
                    <input id="st-to" type="datetime-local" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
                </div>
                <button type="button" className="btn btn-primary" disabled={loading} onClick={() => load()}>Show route</button>
                <div className="btn-group">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => preset("today")}>Today</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => preset("yesterday")}>Yesterday</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => preset("24h")}>Last 24 h</button>
                </div>
            </div>

            {error ? <div className="alert alert-danger py-2">{error}</div> : null}

            {result && !error ? (
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="custom-badge badge-verified">{result.distanceKm} km</span>
                    <span className="custom-badge badge-verified">{result.count} locations{result.shown < result.count ? ` (showing ${result.shown})` : ""}</span>
                    {points.length > 1 ? <span className="custom-badge badge-verified">{durationText(points[0].at, points[points.length - 1].at)} between first and last</span> : null}
                    <span className="d-inline-flex align-items-center gap-1 small text-muted"><i className="st-key" style={{ background: SHIFT_COLOR }} /> on shift</span>
                    <span className="d-inline-flex align-items-center gap-1 small text-muted"><i className="st-key" style={{ background: TASK_COLOR }} /> on a delivery / pick-up</span>
                </div>
            ) : null}

            <div className="st-route-map card border-0 shadow-sm rounded-4 mb-3">
                {loadError ? <div className="p-4 text-danger">The map could not be loaded. Check the Google Maps key.</div> : !isLoaded || loading ? <Skeleton height="100%" /> : (
                    <GoogleMap mapContainerStyle={MAP_STYLE} center={{ lat: 12.9716, lng: 77.5946 }} zoom={12} options={MAP_OPTIONS} onLoad={(map) => { mapRef.current = map; if (points.length) fitTo(map, window.google, points.map(toLatLng)); }}>
                        {segments.map((s, i) => (
                            <Polyline key={i} path={s.path} options={{ strokeColor: s.onTask ? TASK_COLOR : SHIFT_COLOR, strokeOpacity: 0.85, strokeWeight: 5 }} />
                        ))}
                        {points.length ? <Marker position={toLatLng(points[0])} title="First location" label={{ text: "S", color: "#fff", fontWeight: "700" }} /> : null}
                        {points.length > 1 ? <Marker position={toLatLng(points[points.length - 1])} title="Last location" label={{ text: "E", color: "#fff", fontWeight: "700" }} /> : null}
                        {here ? (
                            <Marker
                                position={toLatLng(here)}
                                zIndex={20}
                                icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#ffffff", fillOpacity: 1, strokeColor: here.taskId ? TASK_COLOR : SHIFT_COLOR, strokeWeight: 5 }}
                            />
                        ) : null}
                    </GoogleMap>
                )}
            </div>

            {points.length > 1 ? (
                <div className="card border-0 shadow-sm rounded-4 p-3">
                    <div className="d-flex flex-wrap align-items-center gap-3">
                        <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2" onClick={() => { if (cursor >= points.length - 1) setCursor(0); setPlaying((p) => !p); }}>
                            {playing ? <><FaPause /> Pause</> : <><FaPlay /> Play</>}
                        </button>
                        <select className="form-select form-select-sm w-auto" value={speed} onChange={(e) => setSpeed(Number(e.target.value))} aria-label="Playback speed">
                            {SPEEDS.map((s) => <option key={s} value={s}>{s}x</option>)}
                        </select>
                        <input type="range" className="form-range flex-grow-1" min={0} max={points.length - 1} value={Math.min(cursor, points.length - 1)} onChange={(e) => { setPlaying(false); setCursor(Number(e.target.value)); }} aria-label="Position in route" />
                    </div>
                    {here ? (
                        <div className="small text-muted mt-2 d-flex flex-wrap gap-3">
                            <span>{formatDateTime(here.at)}</span>
                            {Number.isFinite(here.speed) ? <span>{Math.round(here.speed * 3.6)} km/h</span> : null}
                            {Number.isFinite(here.accuracy) ? <span>accuracy {here.accuracy} m</span> : null}
                            {here.taskId ? <span>on task <Link to={`/tasks/${here.taskId}`}>{here.taskId}</Link></span> : <span>on shift</span>}
                        </div>
                    ) : null}
                </div>
            ) : (!loading && !error ? <p className="text-muted">No locations were recorded for this period. Staff share location only while checked in or on a task, and positions are kept for 30 days.</p> : null)}
        </>
    );
};

export default StaffRoute;
